import { SHADING_MODEL } from '../config';
import { HullIndex } from './hullIndex';
import { emptyResult, OcclusionModel } from './occlusion';
import { buildingShadowHull, pointInPolygon, projectToGround, type SunVector } from './shadow';
import {
  EMPTY_STATS,
  type CanopyStats,
  type SelectedBuilding,
  type ShadeMode,
  type ShadingModel,
} from './types';

export interface CanopyBuffers {
  /** Canopy points, in place. */
  canopyPositions: Float64Array;
  canopyColors: Uint8Array;
  /** The same points projected onto the ground along the sun ray. */
  shadowPositions: Float64Array;
  /**
   * RGBA, not RGB: the alpha channel hides the ground dots of canopy that is
   * itself in shade. See the assignment in buildCanopyBuffers for why.
   */
  shadowColors: Uint8Array;
  length: number;
  /**
   * Ground shadow polygons worth drawing: the buildings that some canopy point
   * actually interacts with, plus any the user has focused.
   *
   * Every building in view is classified against, but drawing all of them
   * (269 at z17, overlapping) turns the map into mud and explains nothing.
   * Showing only the shadows that produced a color keeps the picture legible
   * and makes the orange and blue self-evident.
   */
  hulls: Array<Array<[number, number]>>;
  stats: CanopyStats;
}

/**
 * Everything classification needs of a tree: its cloud as a flat
 * `[lon, lat, height, ...]` Float64Array (see lib/pointcloud.ts).
 */
export interface CanopyCloud {
  points: Float64Array;
}

export const EMPTY_BUFFERS: CanopyBuffers = {
  canopyPositions: new Float64Array(0),
  canopyColors: new Uint8Array(0),
  shadowPositions: new Float64Array(0),
  /** RGBA; see CanopyBuffers. */
  shadowColors: new Uint8Array(0),
  length: 0,
  hulls: [],
  stats: EMPTY_STATS,
};

type RGB = [number, number, number];

/**
 * Point colors: one hue per class, ramped from a deep step at ground level to
 * a light one at canopy top so height still reads as brightness.
 *
 * Green now means foliage and nothing else. It used to do double duty as
 * "shading open ground" -- the largest analysis class, at roughly half of all
 * points -- which made the measurement look like plain unanalyzed canopy and
 * defeated the purpose of coloring it at all. The three analysis classes are
 * the Okabe-Ito colorblind-safe set, which contains no green to confuse with.
 *
 * The 2023 ramps these replace were three unrelated per-channel formulas; the
 * green one squared its height term and overflowed past 255 above ~18 m, so the
 * tallest canopy wrapped around to dark. Interpolating between two fixed steps
 * cannot overflow, which is why there is no longer a clamp here.
 *
 * Midpoints land on the legend swatches in styles/app.css -- keep them in step.
 */
const RAMPS = {
  other: { lo: [150, 70, 118] as RGB, hi: [255, 172, 216] as RGB }, // rose  #cc79a7
  shading: { lo: [176, 110, 0] as RGB, hi: [255, 208, 52] as RGB }, // amber #e69f00
  shaded: { lo: [0, 60, 116] as RGB, hi: [60, 168, 240] as RGB }, // blue  #0072b2
  foliage: { lo: [54, 120, 34] as RGB, hi: [160, 240, 96] as RGB }, // green #6bc43a
} as const;

/** Height at which a ramp reaches its lightest step, in metres. */
const RAMP_TOP_M = 22;

function colorFor(kind: keyof typeof RAMPS, z: number): RGB {
  const { lo, hi } = RAMPS[kind];
  const t = z <= 0 ? 0 : z >= RAMP_TOP_M ? 1 : z / RAMP_TOP_M;
  return [
    (lo[0] + (hi[0] - lo[0]) * t) | 0,
    (lo[1] + (hi[1] - lo[1]) * t) | 0,
    (lo[2] + (hi[2] - lo[2]) * t) | 0,
  ];
}

const GREY: RGB = [169, 169, 169];

/**
 * Classifies every selected canopy point against the buildings around it and
 * packs the result into typed arrays deck.gl can upload directly.
 *
 * `buildings` is the whole shading scope: every building passed in casts a
 * shadow and is counted. The caller decides what that scope is -- the whole
 * viewport by default, or just the buildings the user clicked. Color and
 * counts therefore always agree, which is what makes "how much shade does this
 * tree throw on THAT building" answerable by clicking it.
 *
 * Merging all trees into one buffer pair (rather than the 2023 approach of two
 * deck.gl layers per tree) keeps the layer count flat at two regardless of how
 * many trees are loaded.
 */
export function buildCanopyBuffers(
  trees: readonly CanopyCloud[],
  buildings: SelectedBuilding[],
  sun: SunVector,
  shadeMode: ShadeMode,
  /** Whether `buildings` is a user-chosen subset rather than the whole view. */
  focused = false,
  model: ShadingModel = SHADING_MODEL,
): CanopyBuffers {
  const hulls = buildings.map((b) => buildingShadowHull(b.footprint, b.heightRoofFeet, sun));
  const index = new HullIndex(hulls);
  const analysis = shadeMode === 'analysis';
  // The hulls stay the broad phase under either model; only what happens to a
  // candidate afterwards differs.
  const occlusion = model === 'occluded' ? new OcclusionModel(buildings, index, sun) : null;

  let total = 0;
  for (const tree of trees) total += tree.points.length / 3;

  const baseStats: CanopyStats = {
    ...EMPTY_STATS,
    trees: trees.length,
    buildings: buildings.length,
    focused,
    model,
  };

  if (total === 0) {
    return { ...EMPTY_BUFFERS, hulls: focused ? hulls : [], stats: baseStats };
  }

  // Hull indices some canopy point landed in; only these get drawn.
  const active = new Set<number>();

  const canopyPositions = new Float64Array(total * 3);
  const canopyColors = new Uint8Array(total * 3);
  const shadowPositions = new Float64Array(total * 3);
  const shadowColors = new Uint8Array(total * 4);

  const stats: CanopyStats = { ...baseStats, points: total };

  // Per-point scratch, hoisted out of the loop: one result object for the
  // occluded path, and the 2023 path's flags, so that its visitor can be
  // created once rather than half a million times.
  const result = emptyResult();
  let skyLon = 0;
  let skyLat = 0;
  let sawShaded = false;
  let sawShading = false;

  /**
   * The 2023 test, per candidate building.
   *
   * Both halves must hit **the same** building, exactly as
   * legacy/js/nycTreesSingleShadow.js:1288 evaluates `insideGround && insideSky`
   * inside its per-hull loop. The port lost that: it called `hullAt` twice, and
   * `hullAt` returns the first hull containing the point, so the two calls could
   * answer about different buildings. With hundreds of overlapping hulls "inside
   * *any* hull" is nearly always true, which is why viewport-wide classification
   * reported 96.8% of canopy in shade where the published model says about 42%.
   */
  const visitGround = (h: number): void => {
    active.add(h);
    if (pointInPolygon([skyLon, skyLat], hulls[h])) sawShaded = true;
    else sawShading = true;
  };

  let i = 0;
  for (const tree of trees) {
    const cloud = tree.points;

    // The clouds are already in deck.gl's [lon, lat, height] order and the sun
    // does not move canopy, so this half of the render buffer is a block copy.
    canopyPositions.set(cloud, i * 3);

    for (let p = 0; p < cloud.length; p += 3) {
      const lon = cloud[p];
      const lat = cloud[p + 1];
      const z = cloud[p + 2];

      let kind: 'shaded' | 'shading' | 'other' = 'other';
      let shadowLon: number;
      let shadowLat: number;
      let shadowZ = 0;

      if (occlusion) {
        occlusion.classify(lon, lat, z, result);
        kind = result.kind;
        shadowLon = result.hitLon;
        shadowLat = result.hitLat;
        shadowZ = result.hitHeight;
        if (result.building >= 0) active.add(result.building);
      } else {
        const [gLon, gLat] = projectToGround(lon, lat, z, sun);
        shadowLon = gLon;
        shadowLat = gLat;

        // A point shades a building when its shadow lands on one but the point
        // itself is not already inside that building's shadow; if both are true
        // the canopy is sitting in shade rather than casting it.
        if (index.size > 0) {
          skyLon = lon;
          skyLat = lat;
          sawShaded = false;
          sawShading = false;
          index.hullsAt(gLon, gLat, visitGround);
          kind = sawShaded ? 'shaded' : sawShading ? 'shading' : 'other';
        }
      }

      if (kind === 'other') stats.other++;
      else if (kind === 'shaded') stats.shaded++;
      else stats.shading++;

      const o = i * 3;

      // Under the occluded model this is where the shadow actually lands, roof
      // and facade included; under the 2023 model it is always the ground.
      shadowPositions[o] = shadowLon;
      shadowPositions[o + 1] = shadowLat;
      shadowPositions[o + 2] = shadowZ;

      // Foliage mode drops the analysis from both layers at once: plain green
      // canopy over a neutral shadow, so nothing on screen reads as a measure.
      const canopyRgb = analysis ? colorFor(kind, z) : colorFor('foliage', z);
      canopyColors[o] = canopyRgb[0];
      canopyColors[o + 1] = canopyRgb[1];
      canopyColors[o + 2] = canopyRgb[2];

      /*
        Canopy that is itself in shade casts no shadow, so it gets no shadow
        dot.

        Every point used to get one, colored by its class, which put large blue
        patches on the ground -- and blue means "in shade". Read literally that
        says a leaf with no sun on it is darkening the pavement, which it is
        not: the ground under a building's shadow is already dark and the leaf
        removes nothing further. Only the dots that remain -- shading ground,
        and shading a building, the latter now landing on the roof or facade it
        actually reaches -- are shade being added.

        The tally is unaffected: it counts leaves by class, not ground area, so
        "canopy in shade" still reads the same. This is only what gets drawn.

        Left visible in foliage mode, where the shadow is a flat grey shape cue
        and the panel says the analysis is hidden. Carving gaps in it by class
        would be exactly the implicit measurement that mode exists to avoid.
      */
      const s = i * 4;
      const shadowRgb = analysis ? colorFor(kind, z) : GREY;
      shadowColors[s] = shadowRgb[0];
      shadowColors[s + 1] = shadowRgb[1];
      shadowColors[s + 2] = shadowRgb[2];
      shadowColors[s + 3] = analysis && kind === 'shaded' ? 0 : 255;

      i++;
    }
  }

  const drawn = hulls.filter((_, i) => active.has(i) || focused);

  return {
    canopyPositions,
    canopyColors,
    shadowPositions,
    shadowColors,
    length: total,
    hulls: drawn,
    stats,
  };
}
